import { ContentLayout, LeadForm, LeadFormIntegrated, TemplateWrapper } from "@2u-landing-pages/components";
import { usePageState } from "@2u-landing-pages/providers";
import {
  ContentfulImageConfig,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { Container, logEvent } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { FormProps } from "../../Form.types";
// @ts-ignore
import styles from "./Default.module.scss";

interface CustomCSSProperties extends React.CSSProperties {
  "--progress-bar-color"?: string;
  "--question-color"?: string;
  "--button-background-color"?: string;
  "--button-text-color"?: string;
}

interface EduListingPayload {
  form_data_package_id: string;
  image_url: string;
  name: string;
  main_content?: TypeContent_fullWithoutUnresolvableLinksResponse;
  formFootnote?: TypeFormWithoutUnresolvableLinksResponse["fields"]["footnote"];
  section_background_image?: TypeFormWithoutUnresolvableLinksResponse["fields"]["section_background_image"];
  colors: {
    button_background_color?: string;
    button_hover_state?: string;
    button_text_color?: string;
    progress_bar_color?: string;
    question_color?: string;
    section_background_color?: string;
  };
}

interface DrawerFormData {
  main_content: TypeContent_fullWithoutUnresolvableLinksResponse | undefined;
}

export const Default: React.FC<FormProps> = ({
  main_content,
  section_background_color,
  section_background_image,
  image_config,
  embedded,
  taxi_package_id,
  footnote,
  topSlot,
  progress_bar_color,
  question_color,
  button_background_color,
  button_text_color,
  button_hover_state,
  inDrawer,
  phoneNumber,
  parseMetadata,
  isLoading,
  thank_you_page_url,
  form_type,
  mode = "single-program",
}) => {
  const { state } = usePageState();
  const [data, setData] = useState<EduListingPayload | undefined>();

  let sectionBackgroundColor = section_background_color;
  if (mode === "multi-program") {
    sectionBackgroundColor = "--surface-primary-1";
  } else if (inDrawer && data?.colors?.section_background_color) {
    sectionBackgroundColor = data.colors.section_background_color as typeof section_background_color;
  }

  const style: CustomCSSProperties = useMemo(() => {
    const styleObj: CustomCSSProperties = {};

    // Use colors from event.detail if inDrawer is true and data exists, otherwise use props
    const colors = inDrawer ? data?.colors : {};
    const progressBarColor = colors?.progress_bar_color ?? progress_bar_color;
    const questionColor = colors?.question_color ?? question_color;
    const buttonBackgroundColor = colors?.button_background_color ?? button_background_color;
    const buttonTextColor = colors?.button_text_color ?? button_text_color;

    if (progressBarColor) styleObj["--progress-bar-color"] = `var(${progressBarColor})`;
    if (questionColor) styleObj["--question-color"] = `var(${questionColor})`;
    if (buttonBackgroundColor) styleObj["--button-background-color"] = `var(${buttonBackgroundColor})`;
    if (buttonTextColor) styleObj["--button-text-color"] = `var(${buttonTextColor})`;

    return styleObj;
  }, [inDrawer, data?.colors, progress_bar_color, question_color, button_background_color, button_text_color]);

  useEffect(() => {
    const handleEduListing = ((event: CustomEvent<EduListingPayload>) => {
      logEvent("open_inline_drawer_form", event.detail);
      setData(event.detail);
    }) as EventListener;

    window.addEventListener("eduListing", handleEduListing);
    return () => {
      window.removeEventListener("eduListing", handleEduListing);
    };
  }, []);

  useEffect(() => {
    if (!state.isDrawerOpen) setData(undefined);
  }, [state.isDrawerOpen]);

  const renderForm = () => {
    switch (form_type) {
      case "integrated-typ":
        return (
          <LeadFormIntegrated
            isLoading={isLoading}
            className={cn(styles[embedded ? "embeddedLeadForm" : "leadForm"], `btn-${button_hover_state || "dark"}`)}
            applyDomain={taxi_package_id}
            leadFormType="TY"
            phoneNumber={phoneNumber}
            parseMetadata={parseMetadata}
            footnote={footnote}
          />
        );
      case "integrated-plp":
        return (
          <LeadFormIntegrated
            isLoading={isLoading}
            className={cn(styles[embedded ? "embeddedLeadForm" : "leadForm"], `btn-${button_hover_state || "dark"}`)}
            packageId={taxi_package_id}
            thankYouURL={thank_you_page_url}
            leadFormType="LP"
            phoneNumber={phoneNumber}
            parseMetadata={parseMetadata}
            footnote={footnote}
          />
        );
      default:
        return (
          <LeadForm
            key={`${inDrawer && data?.form_data_package_id ? data?.form_data_package_id : taxi_package_id}-${
              state.isDrawerOpen ? "open" : "closed"
            }`}
            isLoading={isLoading}
            className={cn(styles[embedded ? "embeddedLeadForm" : "leadForm"], `btn-${button_hover_state || "dark"}`)}
            packageId={inDrawer && data?.form_data_package_id ? data?.form_data_package_id : taxi_package_id || ""}
            footnote={inDrawer && data?.form_data_package_id ? data?.formFootnote : footnote}
            phoneNumber={phoneNumber}
            parseMetadata={parseMetadata}
          />
        );
    }
  };

  const formColumns = (
    <>
      <ContentLayout
        className={styles[embedded ? "embeddedContent" : "content"]}
        content={
          (inDrawer && data?.main_content
            ? data?.main_content
            : main_content) as unknown as TypeContent_fullWithoutUnresolvableLinksResponse
        }
        heading={{
          variant: embedded ? "heading-lg" : "heading-2xl",
        }}
        subhead={{
          variant: "subhead-sm",
        }}
        phoneNumber={phoneNumber}
        parseMetadata={parseMetadata}
      />
      {renderForm()}
    </>
  );
  if (embedded)
    return (
      <div className={styles.embeddedContainer} style={style}>
        {formColumns}
      </div>
    );

  return (
    <TemplateWrapper
      padded={!inDrawer}
      drawer={inDrawer}
      backgroundColor={sectionBackgroundColor}
      backgroundImage={
        inDrawer && data?.section_background_image ? data.section_background_image : section_background_image
      }
      image_config={image_config as unknown as ContentfulImageConfig}
      style={style}
    >
      {topSlot}
      <Container>
        <Container
          flex
          direction="column"
          lg={{ direction: "row" }}
          className={cn(styles.formContainer, {
            [styles.formContainerInDrawer]: inDrawer,
          })}
        >
          {formColumns}
        </Container>
      </Container>
    </TemplateWrapper>
  );
};
